import { faBookmark, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSavedArticles, SavedArticle } from "../../contexts/SavedArticlesContext";

type SaveButtonProps = {
  article: Omit<SavedArticle, "savedAt">;
  /** "overlay" - small square icon plate; "action" - text+icon button */
  variant?: "overlay" | "action";
  className?: string;
  /** shows a small + badge on overlay icon when article is not yet saved */
  showAddHint?: boolean;
};

const SaveButton = ({
  article,
  variant = "overlay",
  className = "",
  showAddHint = false,
}: SaveButtonProps) => {
  const { isSaved, toggleSave } = useSavedArticles();
  const saved = isSaved(article.id);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSave(article);
  };

  if (variant === "action") {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label={saved ? "Remove from saved" : "Save for later"}
        className={`inline-flex h-10 items-center gap-2 px-4 font-display text-[15px] font-semibold cursor-pointer transition-colors duration-150 ${
          saved
            ? "bg-signal text-white hover:bg-[#0047CC]"
            : "bg-theme-surface text-theme-text hover:bg-signal hover:text-white"
        } ${className}`}
      >
        <FontAwesomeIcon icon={faBookmark} className="h-3.5 w-3.5" />
        <span>{saved ? "Saved" : "Save Articles to Read List"}</span>
      </button>
    );
  }

  // On Air: square plates. Over a thumbnail the plate is stage navy (navy in
  // both themes, like every overlay on a photo); beside an article it is a
  // surface plate. Saved turns either one signal blue.
  const overlayClassName = showAddHint
    ? `relative flex items-center justify-center w-8 h-8 rounded-[3px] transition-colors duration-150 ${
        saved
          ? "bg-signal text-white hover:bg-[#0047CC]"
          : "bg-theme-surface text-theme-text hover:bg-signal hover:text-white"
      }`
    : `flex items-center justify-center w-7 h-7 rounded-[2px] transition-colors duration-150 ${
        saved ? "bg-signal text-white hover:bg-[#0047CC]" : "bg-stage-deep/80 text-white hover:bg-signal"
      }`;

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={saved ? "Remove from saved" : "Save for later"}
      className={`${overlayClassName} ${className}`}
    >
      <FontAwesomeIcon icon={faBookmark} className={showAddHint ? "text-sm" : "text-xs"} />
      {showAddHint && !saved ? (
        <span className="pointer-events-none absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center bg-signal text-white">
          <FontAwesomeIcon icon={faPlus} className="text-[7px]" />
        </span>
      ) : null}
    </button>
  );
};

export default SaveButton;
